<script setup lang="ts">
import CareIcon from '@/components/references/CareIcon.vue'
import UIBrow from '@/components/ui/UIBrow.vue'
import UISpecsList from '@/components/ui/UISpecsList.vue'
import UISpecsListItem from '@/components/ui/UISpecsListItem.vue'
import UIToolTip from '@/components/ui/UIToolTip.vue'
import useLanguage from '@/composables/useLanguage'
import { getRemedySize } from '@/utils/remedy'
import { IRemedyCost, TFootprintPlacement } from '@lifk/types'
import { roundAndTruncate } from '@lifk/utils'
import { PhLock } from '@phosphor-icons/vue'
import { computed, ref } from 'vue'

const i18n = useLanguage('GuidebookRemedySpecs')
const props = defineProps<{
  /** What budget should be displayed for the remedy, if any */
  budgets?: IRemedyCost[]
  /** What square footage should be used to calculate and display the remedy size, if any? */
  footprint?: number
  /** What footprint placement label should be shown, if any? */
  footprintPlacement?: TFootprintPlacement
  /** Should the footprint size and related fields be editable? */
  footprintIsEditable?: boolean
}>()
const careIsHovered = ref(false)
const sizeIsHovered = ref(false)

const remedySize = computed(() =>
  props.footprint ? getRemedySize(props.footprint) : undefined
)

const remedySizeVerbose = computed(() => {
  switch (remedySize.value) {
    case 'XL':
      return i18n.value.extraLarge
    case 'L':
      return i18n.value.large
    case 'M':
      return i18n.value.medium
    case 'S':
      return i18n.value.small
    default:
      return i18n.value.medium
  }
})

const creationText = computed(() => {
  if (!props.budgets) return undefined

  const creationCost = props.budgets.find(
    (cost) => cost.key === 'cost-creation'
  )
  if (!creationCost?.cost) return undefined

  return `${i18n.value.creation.toUpperCase()}: ~$${roundAndTruncate(creationCost.cost)}`
})

const careText = computed(() => {
  if (!props.budgets) return undefined

  const careCost = props.budgets.find((cost) => cost.key === 'cost-care')
  if (!careCost?.cost) return undefined

  return `${i18n.value.care.toUpperCase()}: ~$${roundAndTruncate(careCost.cost)}${i18n.value.perYear}`
})
</script>

<template>
  <UISpecsList class="max-h-[50px] sm:max-h-none" data-testid="remedy-specs">
    <div
      v-if="creationText || careText"
      class="flex flex-col gap-2 md:flex-row md:gap-[30px]"
    >
      <UIToolTip
        v-if="creationText"
        :text="
          props.footprintIsEditable
            ? i18n.creationTooltipEditable
            : i18n.creationTooltip
        "
        direction="top"
        alignment="right"
        :max-width-px="700"
        text-size="default"
      >
        <UISpecsListItem
          icon="PhMoney"
          :alt="i18n.creationIcon"
          :text="creationText"
          class="whitespace-nowrap"
        />
      </UIToolTip>
      <UIToolTip
        v-if="careText"
        :text="
          props.footprintIsEditable
            ? i18n.careTooltipEditable
            : i18n.careTooltip
        "
        direction="top"
        alignment="right"
        :max-width-px="700"
        text-size="default"
      >
        <UISpecsListItem
          :text="careText"
          class="whitespace-nowrap"
          @mouseover="careIsHovered = true"
          @mouseleave="careIsHovered = false"
        >
          <template #customIcon
            ><CareIcon
              :weight="careIsHovered ? 'duotone' : 'regular'"
              class="shrink-0"
              role="img"
              :aria-label="i18n.careIcon"
          /></template>
        </UISpecsListItem>
      </UIToolTip>
    </div>
    <div class="flex flex-col gap-2 md:flex-row md:gap-[30px]">
      <UIToolTip
        v-if="footprint"
        :text="
          props.footprintIsEditable
            ? i18n.footprintTooltipEditable
            : i18n.footprintTooltip
        "
        direction="top"
        alignment="right"
        :max-width-px="700"
        text-size="default"
      >
        <UISpecsListItem
          :text="`${footprint.toLocaleString()} sqft`"
          class="whitespace-nowrap"
          @mouseover="sizeIsHovered = true"
          @mouseleave="sizeIsHovered = false"
        >
          <template #customIcon>
            <div
              class="border-charcoal border min-w-5 w-5 h-5 rounded-[2px] py-1"
              :class="{
                'border-dashed': props.footprintIsEditable,
                'bg-slate-tint-5': sizeIsHovered
              }"
              role="img"
              tabindex="0"
              :aria-label="`${i18n.footprint.replace('$size', remedySizeVerbose ?? '')}, ${props.footprintIsEditable ? i18n.editable : i18n.notEditable}`"
            >
              <UIBrow
                :text="remedySize"
                size="sm"
                class="leading-none w-full text-center"
                tabindex="-1"
              />
            </div>
          </template>
          <PhLock
            v-if="!props.footprintIsEditable"
            size="16"
            weight="fill"
            class="fill-charcoal shrink-0"
          />
        </UISpecsListItem>
      </UIToolTip>
      <UIToolTip
        v-if="footprintPlacement"
        :text="i18n.placement"
        direction="top"
        alignment="right"
      >
        <UISpecsListItem
          icon="PhCubeTransparent"
          :alt="i18n.placementIcon"
          :text="footprintPlacement"
          class="whitespace-nowrap"
        />
      </UIToolTip>
    </div>
  </UISpecsList>
</template>
